import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Rating,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { brandColors } from '../theme/theme';
import { getMovieDetailsFromBigQuery } from '../lib/services/bigQuerySearchService';
import { ReportDownloadButton } from '../components/ReportGeneration';

// Reusable Image component with fallback to logo
const ImageWithFallback = ({ src, alt, style, ...props }) => {
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError) {
      console.log('🖼️ Image failed to load, using logo fallback:', { originalSrc: src, alt });
      setHasError(true);
    }
  };

  // If src is null/undefined or has error, use logo
  const finalSrc = (src && !hasError) ? src : '/images/GreenlightAnalytics_Symbol_MidnightBlue.png';

  return (
    <img
      src={finalSrc}
      alt={alt}
      style={style}
      onError={handleError}
      {...props}
    />
  );
};

const MovieDetail = () => {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchMovieDetails = async () => {
      if (!movieId) {
        setError('No movie ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const movieData = await getMovieDetailsFromBigQuery(movieId);
        
        if (movieData) {
          setMovie(movieData);
        } else {
          setError('Movie not found');
        }
      } catch (err) {
        console.error('Failed to fetch movie details:', err);
        setError(`Failed to load movie details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchMovieDetails();
  }, [movieId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBackClick = () => {
    navigate('/film-finder');
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
        >
          Back to Film Finder
        </Button>
      </Container>
    );
  }

  if (!movie) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Movie not found
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
        >
          Back to Film Finder
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Back Button and Download Report */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
        >
          Back to Film Finder
        </Button>
        <ReportDownloadButton 
          filmData={{ 
            survey: { data: [{ 
              title_text: movie.title,
              imdb_id: movieId,
              pct_aware: movie.ratings?.imdb ? movie.ratings.imdb / 10 : 0,
              pct_interest: 0.3, // Placeholder - would come from survey data
              pct_theater: 0.2,  // Placeholder - would come from survey data
              pct_willing_to_pay: 0.15, // Placeholder - would come from survey data
              total_responses: movie.imdb_vote_count || 0
            }]}, 
            imdb: { data: movie } 
          }} 
          size="small"
        />
      </Box>

      {/* Movie Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ImageWithFallback
                src={movie.poster_url}
                alt={movie.title}
                style={{
                  width: '100%',
                  height: 500,
                  objectFit: 'cover',
                  borderRadius: 8
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ mb: 2, color: brandColors.primary }}>
                {movie.title}
              </Typography>
              
              {movie.original_title && movie.original_title !== movie.title && (
                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                  {movie.original_title}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">
                  {movie.year}
                </Typography>
                {movie.release_date && (
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
                    ({new Date(movie.release_date).toLocaleDateString()})
                  </Typography>
                )}
              </Box>

              {movie.runtime_minutes && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1" color="text.secondary">
                    {movie.runtime_minutes} minutes
                  </Typography>
                </Box>
              )}

              {movie.ratings?.imdb && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Rating value={movie.ratings.imdb / 2} precision={0.1} readOnly size="large" />
                  <Typography variant="h6" sx={{ ml: 2 }}>
                    {movie.ratings.imdb}/10
                  </Typography>
                  {movie.imdb_vote_count && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({movie.imdb_vote_count.toLocaleString()} votes)
                    </Typography>
                  )}
                </Box>
              )}

              {movie.genres && movie.genres.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Genres:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {movie.genres.map((genre, index) => (
                      <Chip key={index} label={genre} variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {movie.director && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Director:</strong> {movie.director}
                  </Typography>
                </Box>
              )}

              {movie.writer && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Writer:</strong> {movie.writer}
                  </Typography>
                </Box>
              )}

              {movie.production_company && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Production:</strong> {movie.production_company}
                  </Typography>
                </Box>
              )}

              {movie.countries_of_origin && movie.countries_of_origin.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Country:</strong> {movie.countries_of_origin.join(', ')}
                  </Typography>
                </Box>
              )}

              {movie.languages && movie.languages.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Language:</strong> {movie.languages.join(', ')}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs for detailed information */}
      <Card>
        <CardContent>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Overview" />
            <Tab label="Cast & Crew" />
            <Tab label="Technical Details" />
            <Tab label="Financials" />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Plot</Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {movie.plot || movie.plot_short || movie.plot_medium || movie.plot_long || 'No plot available'}
                </Typography>

                {movie.metacritic_score && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Metacritic Score</Typography>
                    <Typography variant="body1">
                      {movie.metacritic_score}/100
                      {movie.metacritic_vote_count && (
                        <span style={{ color: 'text.secondary', marginLeft: '8px' }}>
                          ({movie.metacritic_vote_count} reviews)
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}

                {movie.rotten_tomatoes_rating && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Rotten Tomatoes</Typography>
                    <Typography variant="body1">
                      {movie.rotten_tomatoes_rating}%
                      {movie.rotten_tomatoes_vote_count && (
                        <span style={{ color: 'text.secondary', marginLeft: '8px' }}>
                          ({movie.rotten_tomatoes_vote_count} reviews)
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Cast & Crew
                </Typography>
                {movie.cast && movie.cast.length > 0 ? (
                  <List>
                    {movie.cast.map((castMember, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar>{castMember.name.charAt(0)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={castMember.name}
                            secondary={`${castMember.role}${castMember.character ? ` as ${castMember.character}` : ''}`}
                          />
                        </ListItem>
                        {index < movie.cast.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No cast information available
                  </Typography>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Technical Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Title Type:</strong> {movie.title_type || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Production Status:</strong> {movie.production_status || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Runtime:</strong> {movie.runtime_minutes ? `${movie.runtime_minutes} minutes` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Color:</strong> {movie.color && movie.color.length > 0 ? movie.color.join(', ') : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Data Source:</strong> {movie.data_source || 'IMDB API'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Financial Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Budget:</strong> {movie.budget ? `$${movie.budget.toLocaleString()}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Domestic Box Office:</strong> {movie.box_office_domestic ? `$${movie.box_office_domestic.toLocaleString()}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>International Box Office:</strong> {movie.box_office_international ? `$${movie.box_office_international.toLocaleString()}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Worldwide Box Office:</strong> {movie.box_office_worldwide ? `$${movie.box_office_worldwide.toLocaleString()}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Opening Weekend:</strong> {movie.opening_weekend ? `$${movie.opening_weekend.toLocaleString()}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Widest Release:</strong> {movie.widest_release ? `${movie.widest_release} theaters` : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default MovieDetail;
